<template>
	<label class="relative flex flex-col">
		<slot>
			<div
				class="w-full overflow-hidden rounded-xl border-2 border-dashed border-company p-4 duration-300"
				:class="{
					'hover:bg-gray-100': !src
				}"
			>
				<div v-if="!src" class="flex flex-col items-center text-company">
					<Icon name="mdi:attachment" class="h-6 w-6" />
					<span class="mt-1 text-sm">
						{{ $t("upload_n", { name }) }}
					</span>
				</div>
				<div v-if="src" class="flex flex-col">
					<span class="relative mb-4 self-center">
						<img
							:src="runtimeConfig.public.baseUrl + src"
							alt=""
							class=""
							:class="['h-auto max-h-[200px] w-auto max-w-full rounded-md object-contain', imageClass]"
							:style="imageStyle"
						/>
						<span
							class="border-gray-300 pointer-events-none absolute bottom-0 left-0 right-0 top-0 rounded-md border-2"
						/>
					</span>
					<div
						class="grid"
						:class="{
							'grid-cols-1': disableUpload,
							'grid-cols-2': !disableUpload
						}"
					>
						<div class="flex flex-col items-center">
							<a
								class="flex cursor-pointer flex-col items-center text-company"
								:href="runtimeConfig.public.baseUrl + src"
								target="_blank"
							>
								<Icon name="mdi:download" class="h-6 w-6" />
								<span class="mt-1 text-sm">
									{{ $t("download_n", { name }) }}
								</span>
							</a>
						</div>
						<div v-if="!disableUpload" class="flex flex-col items-center">
							<div class="text-red-500 flex cursor-pointer flex-col items-center" @click="removeFile">
								<Icon name="mdi:delete" class="h-6 w-6" />
								<span class="mt-1 text-sm">
									{{ $t("delete_n", { name }) }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</slot>
		<span v-if="!src && !disableUpload" class="absolute bottom-0 end-0 start-0 top-0 overflow-hidden">
			<input
				ref="setInputImage"
				type="file"
				class="absolute bottom-0 left-0 right-0 top-0 h-full w-full cursor-pointer opacity-0"
				style="transform: scale(10)"
				@change="selectImage($event)"
			/>
		</span>
	</label>
</template>

<script setup lang="ts">
import type { Image } from "~/models/image"

const imageStore = useImageStore()
const runtimeConfig = useRuntimeConfig()
const { $toastErrors } = useNuxtApp()

const emit = defineEmits<{
	change: [Image | null]
	input: [File | null]
	uploading: [boolean]
}>()
const props = withDefaults(
	defineProps<{
		name?: string
		disableUpload?: boolean
		src?: string | null
		imageClass?: unknown
		imageStyle?: any
		size?: number
	}>(),
	{
		size: 1920
	}
)

const removeFile = () => {
	emit("change", null)
	emit("input", null)
}

const onUploadImage = async (file: File) => {
	emit("uploading", true)
	try {
		const image = await imageStore.uploadImage(file, props.size)
		emit("uploading", false)
		return image
	} catch (e) {
		emit("uploading", false)
		$toastErrors(e)
		return null
	}
}
const selectImage = async (event: any) => {
	if (props.disableUpload) return
	const files = [...(event.target.files || event.dataTransfer.files || [])]
	const file = files?.[0] as File | undefined
	if (!file) {
		emit("change", null)
		return
	}
	const f = await onUploadImage(file)
	event.target.value = null
	emit("change", f)
	emit("input", file)
}
</script>
